import { useRef, useState } from "react";
import { createCommandSender } from "./academyApi";
import { checkIdentitySession } from "./identityClient";
import { useIdentity } from "./useIdentity";
import { ACADEMIC_CATALOGUE_CHANGED } from "./useAcademicCatalogue";
import {
  academicLessonRoute,
  lessonFormats,
  useTeachingContent,
  useTeachingCurricula,
  useTeachingPrograms,
} from "./useAcademicTeaching";
import type { AcademicReceipt, AddAcademicLesson } from "./generated/academic-catalogue";

/** Approved controls inside the original Instructor Create lesson dialog. */
export function AcademicLessonFields({
  formId,
  onSaved,
  onBusy,
}: {
  formId: string;
  onSaved: (route: string) => void;
  onBusy: (busy: boolean) => void;
}) {
  const account = useIdentity(),
    programs = useTeachingPrograms();
  const [programId, setProgramId] = useState(""),
    [versionId, setVersionId] = useState(""),
    [moduleId, setModuleId] = useState("");
  const [title, setTitle] = useState(""),
    [outcome, setOutcome] = useState(""),
    [reason, setReason] = useState("");
  const [format, setFormat] = useState<NonNullable<AddAcademicLesson["deliveryFormat"]>>("live_self_paced");
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const versions = useTeachingCurricula(programId),
    content = useTeachingContent(programId, versionId);
  const sender = useRef(createCommandSender({ headerOnly: true })),
    pending = useRef(false);
  const session = account.session;
  const context = session ? `${session.tenantId}:${session.user.id}:${session.workspace.id}` : "";
  const current = useRef(context);
  current.current = context;
  const canManage = !!session?.permissions.includes("curriculum.manage");
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const curriculum = content.data,
      module = curriculum?.modules.find(item => item.id === moduleId);
    if (pending.current || !curriculum || !module || !canManage) return;
    pending.current = true;
    setBusy(true);
    onBusy(true);
    setError("");
    try {
      const actual = await checkIdentitySession();
      if (
        `${actual.tenantId}:${actual.user.id}:${actual.workspace.id}` !== context ||
        current.current !== context
      )
        throw new Error("Your workspace changed. Reopen the lesson dialog in its current scope.");
      if (curriculum.state !== "draft") throw new Error("Select an editable curriculum draft.");
      const input: AddAcademicLesson = {
        title: title.trim(),
        objectives: [outcome.trim()],
        activities: [],
        deliveryFormat: format,
        position: Math.max(0, ...module.lessons.map(item => item.position)) + 1,
        expectedVersion: curriculum.version,
        reason: reason.trim(),
      };
      const receipt = await sender.current<AcademicReceipt>(
        `academic-catalogue/program/${programId}/curricula/${versionId}/modules/${moduleId}/commands/add-lesson`,
        input,
      );
      if (!receipt.componentId)
        throw new Error(
          "The lesson receipt has no component identity. Retain this dialog and retry after restoring the current API.",
        );
      window.dispatchEvent(new Event(ACADEMIC_CATALOGUE_CHANGED));
      onSaved(academicLessonRoute(programId, versionId, receipt.componentId));
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "The lesson could not be created.");
    } finally {
      pending.current = false;
      setBusy(false);
      onBusy(false);
    }
  };
  const message = error || programs.error || versions.error || content.error;
  return (
    <form id={formId} onSubmit={submit}>
      {message && <p role="alert">{message}</p>}
      {(programs.loading || versions.loading || content.loading) && (
        <p role="status">Loading assigned academic definitions…</p>
      )}
      {!canManage && <p role="alert">Current curriculum management permission is required.</p>}
      {!programs.loading && !programs.data?.length && (
        <p>No assigned Programs are available for authoring.</p>
      )}
      <div className="form-row">
        <label className="field-label">
          <span>Lesson title</span>
          <input
            aria-label="Lesson title"
            className="text-field"
            required
            minLength={3}
            maxLength={240}
            value={title}
            disabled={busy}
            onChange={event => setTitle(event.target.value)}
          />
        </label>
        <label className="field-label">
          <span>Programme</span>
          <select
            aria-label="Programme"
            required
            disabled={busy || programs.loading}
            value={programId}
            onChange={event => {
              setProgramId(event.target.value);
              setVersionId("");
              setModuleId("");
            }}
          >
            <option value="">Choose an assigned Programme</option>
            {programs.data?.map(item => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="form-row" style={{ marginTop: 10 }}>
        <label className="field-label">
          <span>Curriculum version</span>
          <select
            aria-label="Curriculum version"
            required
            disabled={busy || versions.loading}
            value={versionId}
            onChange={event => {
              setVersionId(event.target.value);
              setModuleId("");
            }}
          >
            <option value="">Choose an editable draft</option>
            {versions.data
              ?.filter(item => item.state === "draft")
              .map(item => (
                <option key={item.id} value={item.id}>
                  {item.versionLabel} · {item.title}
                </option>
              ))}
          </select>
        </label>
        <label className="field-label">
          <span>Module</span>
          <select
            aria-label="Module"
            required
            disabled={busy || content.loading}
            value={moduleId}
            onChange={event => setModuleId(event.target.value)}
          >
            <option value="">Choose the exact module</option>
            {content.data?.modules.map(item => (
              <option key={item.id} value={item.id}>
                {item.code} · {item.title}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="form-row" style={{ marginTop: 10 }}>
        <label className="field-label">
          <span>Format</span>
          <select
            aria-label="Format"
            disabled={busy}
            value={format}
            onChange={event => setFormat(event.target.value as typeof format)}
          >
            {Object.entries(lessonFormats).map(([key, value]) => (
              <option key={key} value={key}>
                {value}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="field-label" style={{ marginTop: 10 }}>
        <span>Learning outcome</span>
        <textarea
          aria-label="Learning outcome"
          className="text-area"
          required
          minLength={3}
          maxLength={500}
          value={outcome}
          disabled={busy}
          onChange={event => setOutcome(event.target.value)}
        />
      </label>
      <label className="field-label" style={{ marginTop: 10 }}>
        <span>Reason for creation</span>
        <textarea
          aria-label="Reason for creation"
          className="text-area"
          required
          minLength={10}
          maxLength={2000}
          value={reason}
          disabled={busy}
          onChange={event => setReason(event.target.value)}
        />
      </label>
    </form>
  );
}
